import { api, unwrapList } from '../../../shared/lib/axios';
import type { LocationType } from '../../../shared/lib/locations';

export type { LocationType };

/**
 * API client dos cadastros de áreas, cargos e feriados (change
 * `pessoas-pre-requisitos`).
 *
 * O interceptor HATEOAS do backend espalha objetos no envelope
 * (`{ version, _links, ...objeto }`) e põe arrays sob `data`. As
 * listagens paginadas já têm `data` + `pagination` próprios, então
 * chegam como `{ version, _links, data, pagination }`.
 */

export type ActiveFilter = 'true' | 'false' | 'all';

export interface PaginatedList<T> {
  data: T[];
  pagination: { limit: number; offset: number; count: number };
}

export interface OrgUnit {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  defaultCostCenterId: string | null;
  active: boolean;
}

export interface OrgUnitNode extends OrgUnit {
  children: OrgUnitNode[];
}

export interface OrgUnitPayload {
  code: string;
  name: string;
  parentId: string | null;
  defaultCostCenterId: string | null;
}

export interface JobPosition {
  id: string;
  code: string;
  name: string;
  description: string | null;
  cboCode: string | null;
  active: boolean;
}

export interface JobPositionPayload {
  code: string;
  name: string;
  description: string | null;
  cboCode: string | null;
}

export type HolidayType = 'feriado' | 'ponto_facultativo';
export type HolidayScope = 'nacional' | 'estadual' | 'municipal';

export interface Holiday {
  id: string;
  date: string;
  name: string;
  type: HolidayType;
  scope: HolidayScope;
  stateUf: string | null;
  cityId: number | null;
  cityName: string | null;
  recurring: boolean;
  active: boolean;
}

export interface HolidayPayload {
  date: string;
  name: string;
  type: HolidayType;
  scope: HolidayScope;
  stateUf: string | null;
  cityId: number | null;
  recurring: boolean;
}

export interface CostCenterOption {
  id: string;
  code: string;
  name: string;
}

export interface ListParams {
  search?: string;
  active?: ActiveFilter;
  limit?: number;
  offset?: number;
}

export interface ListHolidaysParams {
  year: number;
  scope?: HolidayScope;
  stateUf?: string;
  cityId?: number;
  active?: ActiveFilter;
}

function paginated<T>(body: unknown): PaginatedList<T> {
  const b = (body ?? {}) as Partial<PaginatedList<T>>;
  return {
    data: Array.isArray(b.data) ? b.data : [],
    pagination: b.pagination ?? { limit: 0, offset: 0, count: 0 },
  };
}

function object<T>(body: unknown): T {
  const { version: _v, _links: _l, ...rest } = (body ?? {}) as Record<string, unknown>;
  return rest as T;
}

// --- Áreas ---------------------------------------------------------

export async function listarAreas(params: ListParams): Promise<PaginatedList<OrgUnit>> {
  const { data } = await api.get('/org-units', { params });
  return paginated<OrgUnit>(data);
}

export async function arvoreAreas(): Promise<OrgUnitNode[]> {
  const { data } = await api.get('/org-units/tree');
  return unwrapList<OrgUnitNode>(data);
}

export async function criarArea(payload: OrgUnitPayload): Promise<OrgUnit> {
  const { data } = await api.post('/org-units', payload);
  return object<OrgUnit>(data);
}

export async function atualizarArea(id: string, payload: Partial<OrgUnitPayload>): Promise<OrgUnit> {
  const { data } = await api.patch(`/org-units/${id}`, payload);
  return object<OrgUnit>(data);
}

export async function alterarSituacaoArea(id: string, active: boolean): Promise<OrgUnit> {
  const { data } = await api.patch(`/org-units/${id}/active`, { active });
  return object<OrgUnit>(data);
}

/** Centros de custo ativos para o select de centro de custo padrão. */
export async function listarCentrosDeCusto(): Promise<CostCenterOption[]> {
  const { data } = await api.get('/cost-centers', { params: { limit: 500 } });
  return paginated<CostCenterOption>(data).data;
}

// --- Cargos --------------------------------------------------------

export async function listarCargos(params: ListParams): Promise<PaginatedList<JobPosition>> {
  const { data } = await api.get('/job-positions', { params });
  return paginated<JobPosition>(data);
}

export async function criarCargo(payload: JobPositionPayload): Promise<JobPosition> {
  const { data } = await api.post('/job-positions', payload);
  return object<JobPosition>(data);
}

export async function atualizarCargo(id: string, payload: Partial<JobPositionPayload>): Promise<JobPosition> {
  const { data } = await api.patch(`/job-positions/${id}`, payload);
  return object<JobPosition>(data);
}

export async function alterarSituacaoCargo(id: string, active: boolean): Promise<JobPosition> {
  const { data } = await api.patch(`/job-positions/${id}/active`, { active });
  return object<JobPosition>(data);
}

// --- Feriados ------------------------------------------------------

export async function listarFeriados(params: ListHolidaysParams): Promise<Holiday[]> {
  const { data } = await api.get('/holidays', { params });
  return paginated<Holiday>(data).data;
}

export async function criarFeriado(payload: HolidayPayload): Promise<Holiday> {
  const { data } = await api.post('/holidays', payload);
  return object<Holiday>(data);
}

export async function atualizarFeriado(id: string, payload: Partial<HolidayPayload>): Promise<Holiday> {
  const { data } = await api.patch(`/holidays/${id}`, payload);
  return object<Holiday>(data);
}

export async function alterarSituacaoFeriado(id: string, active: boolean): Promise<Holiday> {
  const { data } = await api.patch(`/holidays/${id}/active`, { active });
  return object<Holiday>(data);
}

export async function gerarFeriados(year: number): Promise<{ created: number; skipped: number }> {
  const { data } = await api.post('/holidays/generate', null, { params: { year } });
  return object<{ created: number; skipped: number }>(data);
}

/** Mensagem de erro da API (string ou lista do ValidationPipe). */
export function mensagemDeErro(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: unknown } } }).response?.data?.message;
  if (typeof msg === 'string') return msg;
  if (Array.isArray(msg) && msg.length > 0) return msg.join(' ');
  return fallback;
}

// --- Localidades (issue #300) --------------------------------------

export interface Location {
  id: string;
  code: string;
  name: string;
  type: LocationType;
  active: boolean;
  clientId: string | null;
  clientName: string | null;
  companyId: string | null;
  companyName: string | null;
}

export interface LocationPayload {
  /** Opcional: sem ele o backend gera um código a partir do nome. */
  code?: string;
  name: string;
  type: LocationType;
  clientId?: string | null;
  companyId?: string | null;
}

export interface ListLocationsParams extends ListParams {
  type?: LocationType;
}

export async function listarLocalidades(params: ListLocationsParams): Promise<PaginatedList<Location>> {
  const { data } = await api.get('/locations', { params });
  return paginated<Location>(data);
}

export async function criarLocalidade(payload: LocationPayload): Promise<Location> {
  const { data } = await api.post('/locations', payload);
  return object<Location>(data);
}

export async function atualizarLocalidade(
  id: string,
  payload: Partial<LocationPayload>,
): Promise<Location> {
  const { data } = await api.patch(`/locations/${id}`, payload);
  return object<Location>(data);
}

export async function alterarSituacaoLocalidade(id: string, active: boolean): Promise<Location> {
  const { data } = await api.patch(`/locations/${id}/active`, { active });
  return object<Location>(data);
}

/** Clientes ativos para vincular a uma localidade do tipo Cliente (RN05). */
export async function listarClientesParaLocalidade(): Promise<Array<{ id: string; name: string }>> {
  const { data } = await api.get('/clients', { params: { limit: 500, active: 'true' } });
  return paginated<{ id: string; name?: string; razaoSocial?: string; nomeFantasia?: string }>(data).data.map(
    (c) => ({ id: c.id, name: c.nomeFantasia?.trim() || c.razaoSocial || c.name || '' }),
  );
}

/** Empresas ativas para vincular a uma localidade de unidade da empresa (RN06). */
export async function listarEmpresasParaLocalidade(): Promise<Array<{ id: string; name: string }>> {
  const { data } = await api.get('/companies', { params: { limit: 500, isActive: true } });
  return paginated<{ id: string; tradeName?: string; corporateName?: string }>(data).data.map(
    (c) => ({ id: c.id, name: c.tradeName || c.corporateName || '' }),
  );
}
